
import { useEffect } from 'react';

interface SEOProps {
  title: string;
  description: string;
  keywords?: string;
  canonical?: string;
  ogType?: string;
  jsonLd?: object | object[];
}

const SITE_URL = import.meta.env.VITE_SITE_URL || 'https://mortgagemoat.com';
const OG_IMAGE = 'https://static.readdy.ai/image/807f7a13fa3a4707437af02957aa93c9/1baf6cd348ab623aee3f3ee6959ea81e.png';

export const useSEO = ({
  title,
  description,
  keywords,
  canonical,
  ogType = 'website',
  jsonLd,
}: SEOProps) => {
  useEffect(() => {
    // Title
    document.title = title;

    const setMeta = (name: string, content: string, isProperty = false) => {
      const attr = isProperty ? 'property' : 'name';
      let el = document.querySelector(`meta[${attr}="${name}"]`) as HTMLMetaElement | null;
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute(attr, name);
        document.head.appendChild(el);
      }
      el.setAttribute('content', content);
    };

    const setLink = (rel: string, href: string) => {
      let el = document.querySelector(`link[rel="${rel}"]`) as HTMLLinkElement | null;
      if (!el) {
        el = document.createElement('link');
        el.setAttribute('rel', rel);
        document.head.appendChild(el);
      }
      el.setAttribute('href', href);
    };

    // Basic meta
    setMeta('description', description);
    if (keywords) setMeta('keywords', keywords);
    setMeta('last-modified', new Date().toISOString().split('T')[0]);

    // Canonical
    const canonicalUrl = canonical ? `${SITE_URL}${canonical}` : SITE_URL;
    setLink('canonical', canonicalUrl);

    // Open Graph
    setMeta('og:type', ogType, true);
    setMeta('og:url', canonicalUrl, true);
    setMeta('og:title', title, true);
    setMeta('og:description', description, true);
    setMeta('og:image', OG_IMAGE, true);

    // Twitter
    setMeta('twitter:card', 'summary_large_image');
    setMeta('twitter:url', canonicalUrl);
    setMeta('twitter:title', title);
    setMeta('twitter:description', description);
    setMeta('twitter:image', OG_IMAGE);

    // JSON-LD
    const existingScripts = document.querySelectorAll('script[data-seo-jsonld]');
    existingScripts.forEach((s) => s.remove());

    if (jsonLd) {
      const schemas = Array.isArray(jsonLd) ? jsonLd : [jsonLd];
      schemas.forEach((schema) => {
        const script = document.createElement('script');
        script.type = 'application/ld+json';
        script.setAttribute('data-seo-jsonld', 'true');
        script.textContent = JSON.stringify(schema);
        document.head.appendChild(script);
      });
    }

    return () => {
      // Cleanup JSON-LD on unmount
      document.querySelectorAll('script[data-seo-jsonld]').forEach((s) => s.remove());
    };
  }, [title, description, keywords, canonical, ogType, jsonLd]);
};

export { SITE_URL };
